import React, { useState, useMemo } from 'react';
import {
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  FormHelperText,
  Chip,
  Box,
  SelectChangeEvent,
  useTheme,
  OutlinedInput,
  TextField,
} from '@mui/material';
import { IDropdownOptions } from '@shared/interface';

export interface MultiSelectOption extends IDropdownOptions { }

interface MultiSelectProps {
  label?: string;
  value: number[];
  onChange: (value: number[]) => void;
  options: MultiSelectOption[];
  error?: string;
  required?: boolean;
  disabled?: boolean;
  placeholder?: string;
  fullWidth?: boolean;
  size?: 'small' | 'medium';
  useOptionId?: boolean;
  'data-testid'?: string;
}

const ITEM_HEIGHT = 48;
const ITEM_PADDING_TOP = 8;

export const MultiSelect: React.FC<MultiSelectProps> = ({
  label,
  value,
  onChange,
  options,
  error,
  required = false,
  disabled = false,
  placeholder,
  fullWidth = true,
  size = 'medium',
  useOptionId = false,
  'data-testid': testId,
}) => {
  const theme = useTheme();
  const [searchQuery, setSearchQuery] = useState('');

  // Filter options based on search query
  const filteredOptions = useMemo(() => {
    if (!searchQuery.trim()) return options;
    const query = searchQuery.toLowerCase();
    return options.filter((option) =>
      option.label.toLowerCase().includes(query)
    );
  }, [options, searchQuery]);

  // Dynamic MenuProps with search field
  const MenuProps = {
    PaperProps: {
      style: {
        maxHeight: ITEM_HEIGHT * 4.5 + ITEM_PADDING_TOP + 56, // Extra space for search field
        width: 300,
      },
    },
    autoFocus: false,
  };

  const handleChange = (event: SelectChangeEvent<number[]>) => {
    const selectedValue = event.target.value;
    onChange(selectedValue as number[]);
  };

  const handleSearchChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setSearchQuery(event.target.value);
  };

  const handleMenuClose = () => {
    setSearchQuery('');
  };

  const handleDelete = (valueToDelete: number) => {
    onChange(value.filter((v) => v !== valueToDelete));
  };

  return (
    <FormControl fullWidth={fullWidth} error={!!error} size={size} disabled={disabled}>
      {label && (
        <InputLabel
          required={required}
          sx={{
            color: theme.palette.text.primary,
            '&.Mui-focused': {
              color: theme.palette.primary.main,
            },
          }}
        >
          {label}
        </InputLabel>
      )}
      <Select
        multiple
        value={value}
        onChange={handleChange}
        onClose={handleMenuClose}
        input={<OutlinedInput label={label || ''} />}
        data-testid={testId}
        MenuProps={MenuProps}
        displayEmpty
        renderValue={(selected) => {
          if (selected.length === 0 && placeholder) {
            return <Box sx={{ color: 'text.secondary' }}>{placeholder}</Box>;
          }
          return (
            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
              {selected.map((val: number) => {
                const intValue = Number(val);
                const option = options.find((opt) =>
                  useOptionId ? opt.optionId === intValue : opt.value === intValue,
                );
                return (
                  <Chip
                    key={val}
                    label={option?.label || String(val)}
                    size="small"
                    onDelete={() => handleDelete(val)}
                    onMouseDown={(event) => {
                      event.stopPropagation();
                    }}
                    sx={{
                      backgroundColor: theme.palette.primary.light,
                      color: theme.palette.primary.contrastText,
                      '& .MuiChip-deleteIcon': {
                        color: theme.palette.primary.contrastText,
                        '&:hover': {
                          color: theme.palette.primary.dark,
                        },
                      },
                    }}
                  />
                );
              })}
            </Box>
          );
        }}
        sx={{
          '& .MuiSelect-select': {
            display: 'flex',
            alignItems: 'center',
          },
        }}
      >
        <Box sx={{ zIndex: 1, px: 1 }}>
          <TextField
            size="small"
            fullWidth
            placeholder="Search..."
            value={searchQuery}
            onChange={handleSearchChange}
            onClick={(e) => e.stopPropagation()}
            onKeyDown={(e) => e.stopPropagation()}
          />
        </Box>
        {filteredOptions.length === 0 ? (
          <MenuItem disabled>
            <Box sx={{ color: 'text.secondary', fontStyle: 'italic' }}>No options found</Box>
          </MenuItem>
        ) : (
          filteredOptions.map((option) => (
            <MenuItem
              key={useOptionId ? option.optionId : option.value}
              value={useOptionId ? option.optionId : option.value}
            // sx={{
            //   '&:hover': {
            //     backgroundColor: theme.palette.action.hover,
            //   },
            //   '&.Mui-selected': {
            //     backgroundColor:
            //       theme.palette.mode === 'light' ? theme.palette.primary.main : theme.palette.primary.light,
            //     color: theme.palette.mode === 'light' ? theme.palette.common.white : undefined,
            //   },
            //   '&.Mui-selected:hover': {
            //     backgroundColor:
            //       theme.palette.mode === 'light' ? theme.palette.primary.dark : theme.palette.primary.light,
            //     color: theme.palette.mode === 'light' ? theme.palette.common.white : undefined,
            //   },
            // }}
            >
              {option.label}
            </MenuItem>
          ))
        )}
      </Select>
      <FormHelperText sx={{ minHeight: '20px', color: error ? theme.palette.error.main : 'transparent' }}>
        {error || ' '}
      </FormHelperText>
    </FormControl>
  );
};
